import { uuid } from "uuidv4";
import {
    getBootstrapFilters,
    getCandybarItemEntities,
    getCheckoutCandybarOrderTotal,
    getCheckoutCinema,
    getCheckoutConfirmationData,
    getCheckoutContext,
    getCheckoutGrandTotal,
    getCheckoutMovie,
    getCheckoutOrderCandybar,
    getCheckoutSession,
    getCheckoutTicketsWithQuantity
} from "../../reducers";
import {
    addCinemaFieldsToEventData,
    addMovieFieldsToEventData,
    addSessionFieldsToEventData,
    dataLayerPush,
    getCandybarOrderContentIds,
    getCinemaExperience
} from "../analytics";
import { CinemaType, MovieType } from "../../types/entities";
import { trackEvent } from "./serverSide";
import { CHECKOUT_CONTEXT_CANDYBAR, CHECKOUT_CONTEXT_TICKETS } from "../../actions/creators/checkout";
import { getMovieAttributes } from "../misc";
import { getCandybarItemPriceWithConfigs } from "../candybar";

export const trackCheckoutSessionLoad = (action, state) => {
    const session = getCheckoutSession(state);
    const cinema = getCheckoutCinema(state);
    const movie = getCheckoutMovie(state);
    const attributes = getBootstrapFilters(state) as unknown as Record<string, { display_name: string }>;

    if (!session || !cinema || !movie) {
        return;
    }

    const eventId = uuid();
    
    dataLayerPush({
        event: 'billboardSessionView',
        eventId,
        action: 'Session',
        label: `${cinema.name} - ${movie.name} - ${session.datetime}`,
    });

    const metaEventData = {
        content_ids: `session_${movie.id}`,
        content_type: 'product',
        content_name: `${cinema.name} - ${movie.name} - ${session.datetime}`,
        currency: 'MXN',
        value: 0,
        content_category: getCheckoutEventContentCategory(cinema, movie, attributes),
    };

    addCinemaFieldsToEventData(metaEventData, cinema);
    addMovieFieldsToEventData(metaEventData, movie);
    addSessionFieldsToEventData(metaEventData, session, movie);

    trackEvent(
        'ViewContent',
        eventId,
        metaEventData
    );

    const googleEvent = {
        event: 'view_item',
        currency: "MXN",
        value: 0,
        items: [
            {
                item_id: metaEventData.content_ids,
                item_name: metaEventData.content_name
            }
        ]
    };

    dataLayerPush(googleEvent);
}

export function trackCheckoutTicketsSelection(action, state) {
    const session = getCheckoutSession(state);
    const cinema = getCheckoutCinema(state);
    const movie = getCheckoutMovie(state);
    const ticketsOrder = getCheckoutTicketsWithQuantity(state);
    const attributes = getBootstrapFilters(state) as unknown as Record<string, { display_name: string }>;

    const metaEventData = {
        content_type: 'product',
        currency: 'MXN',
        content_category: getCheckoutEventContentCategory(cinema, movie, attributes),
        content_ids: 'movie_' + movie.id,
        content_name: `Tickets ${cinema.name} - ${movie.name}`,
        value: ticketsOrder ? ticketsOrder.reduce((acc, ticket) => acc + (ticket.price * ticket.qty), 0) / 100: 0
    };

    addCinemaFieldsToEventData(metaEventData, cinema);
    addMovieFieldsToEventData(metaEventData, movie);
    addSessionFieldsToEventData(metaEventData, session, movie);

    trackEvent(
        'AddToCart',
        uuid(),
        metaEventData
    );

    const googleEvent = {
        event: 'add_to_cart',
        currency: "MXN",
        value: metaEventData.value,
        items: getTicketsItemsForGoogleEvent(state, ticketsOrder)
    };

    dataLayerPush(googleEvent);
}

function getTicketsItemsForGoogleEvent(state, order) {
    if (!order) {
        return [];
    }

    return order.filter(ticket => ticket.qty > 0).map(ticket => ({
        item_id: `ticket_${ticket.id}`,
        item_name: ticket.name,
        quantity: ticket.qty,
        price: ticket.price / 100
    }));
}

export function trackCheckoutCandybarSelection(action, state) {
    const order = getCheckoutOrderCandybar(state);
    const total = getCheckoutCandybarOrderTotal(state);
    const cinema = getCheckoutCinema(state);
    const context = getCheckoutContext(state);
    const hasTickets = CHECKOUT_CONTEXT_TICKETS === context;
    const eventId = uuid();

    const meatEventData: { [key: string]: any } = {
        content_ids: getCandybarOrderContentIds(order),
        content_type: 'product',
        currency: 'MXN',
        value: total / 100,
        content_category: 'Food & Drinks'
    };
    
    addCinemaFieldsToEventData(meatEventData, cinema);

    if (hasTickets) {
        meatEventData.content_name = 'Cinema > Tickets > Concessions';
    } else {
        meatEventData.content_name = 'Cinema > Concessions';
    }

    trackEvent(
        'AddToCart',
        eventId,
        meatEventData
    );

    const googleEvent = {
        event: 'add_to_cart',
        currency: meatEventData.currency,
        value: meatEventData.value,
        items: getCandybarItemsForGoogleEvent(state, order)
    };
    
    dataLayerPush(googleEvent);
}

function getCandybarItemsForGoogleEvent(state, order) {
    if (!order) {
        return [];
    }

    const allItems = getCandybarItemEntities(state);
    const items: { item_id: string, item_name: string, quantity: number, price: number }[] = [];

    Object.keys(order).forEach(itemId => {
        const item = allItems[itemId];
        Object.keys(order[itemId]).forEach(unitId => {
            const itemPrice = getCandybarItemPriceWithConfigs(state, itemId, order[itemId][unitId].config);
            items.push({
                item_id: `candybar_${itemId}`,
                item_name: item.name,
                quantity: 1, // @todo Get real quantity
                price: itemPrice / 100
            });
        });
    });

    return items;
}

export function trackCheckoutPaymentMethodSelection(action, state) {
    const metaEventData = getCheckoutPaymentMetaEventData(state);
    metaEventData.content_name = 'Checkout ' + metaEventData.content_name;

    trackEvent(
        'InitiateCheckout',
        uuid(),
        metaEventData
    );

    const googleEvent: { [key: string]: any } = getCheckoutPaymentGoogleEventdata(state);
    googleEvent.event = 'begin_checkout';

    dataLayerPush(googleEvent);
}

export function trackCheckoutConfirmation(action, state) {
    const context = getCheckoutContext(state);

    if ([CHECKOUT_CONTEXT_TICKETS, CHECKOUT_CONTEXT_CANDYBAR].indexOf(context) === -1) {
        return;
    }
    
    const confirmationData = getCheckoutConfirmationData(state);
    const metaEventData = getCheckoutPaymentMetaEventData(state);
    metaEventData.content_name = 'Purchase ' + metaEventData.content_name;

    trackEvent(
        'Purchase',
        uuid(),
        metaEventData
    );

    const googleEvent: { [key: string]: any } = getCheckoutPaymentGoogleEventdata(state);
    googleEvent.event = 'purchase';
    googleEvent.transaction_id = `tickets_${confirmationData.id}`;

    dataLayerPush(googleEvent);
}

export function getCheckoutEventContentCategory(cinema: CinemaType, movie: MovieType, attributes: { [key: string]: { display_name: string } }): string {
    const movieGenres = movie.info && movie.info.genre ? movie.info.genre : [];
    const movieAttributes = getMovieAttributes(movie);
    let sessionFormats = movieAttributes ? movieAttributes.filter(attr => !attr.startsWith('lang_')) : [];
    let sessionLanguages = movieAttributes ? movieAttributes.filter(attr => attr.startsWith('lang_')) : [];

    sessionFormats = attributes ? sessionFormats.map(format => {
        return (format in attributes) ? attributes[format].display_name : format;
    }) : sessionFormats;

    sessionLanguages = attributes ? sessionLanguages.map(lang => {
        return (lang in attributes) ? attributes[lang].display_name : lang;
    }) : sessionLanguages;

    return [
        'Cinema',
        getCinemaExperience(cinema),
        movieGenres.join(', '),
        movie.name,
        sessionFormats.join(', '),
        sessionLanguages.join(', ')
    ].join(' > ');
}

function getCheckoutPaymentMetaEventData(state) {
    const isCandybarOnlyPurchase = CHECKOUT_CONTEXT_CANDYBAR === getCheckoutContext(state);
    const session = getCheckoutSession(state);
    const cinema = getCheckoutCinema(state);
    const movie = getCheckoutMovie(state);
    const attributes = getBootstrapFilters(state) as unknown as Record<string, { display_name: string }>;
    const total = getCheckoutGrandTotal(state);
    const candybarOrder = getCheckoutOrderCandybar(state);
    let contentIds = [`movie_${movie.id}`];
 
    if (candybarOrder) {
        contentIds = contentIds.concat(getCandybarOrderContentIds(candybarOrder));
    }

    const eventData = {
        content_type: 'product',
        currency: 'MXN',
        content_category: getCheckoutEventContentCategory(cinema, movie, attributes),
        content_name: `${cinema.name} - ${movie.name}`,
        content_ids: contentIds,
        value: total / 100,
    };

    addCinemaFieldsToEventData(eventData, cinema);

    if (!isCandybarOnlyPurchase) {
        addMovieFieldsToEventData(eventData, movie);
        addSessionFieldsToEventData(eventData, session, movie);
    }

    return eventData;
}

function getCheckoutPaymentGoogleEventdata(state) {
    const candybarOrder = getCheckoutOrderCandybar(state);
    const ticketsOrder = getCheckoutTicketsWithQuantity(state);
    const total = getCheckoutGrandTotal(state);
    const items = getTicketsItemsForGoogleEvent(state, ticketsOrder)
        .concat(getCandybarItemsForGoogleEvent(state, candybarOrder));

    const eventData = {
        currency: 'MXN',
        value: total / 100,
        items
    };

    return eventData;
}